import * as React from 'react';
import * as UI from 'core/ui';
import * as Images from 'pos/Images';
import * as CloseDayStore from './CloseDayStore';

import { Permission, Res, TicketSummaryDTO } from 'core/api';
import { ActionButtonDefinition, PosScreenClassComponent, ScreenSize } from 'pos/ui';
import { Result, SessionProvider } from 'core/infrastructure';
import { Command } from 'pos/common';
import { Flex } from 'core/ui';
import { RestaurantFacade } from 'pos/facades';
import { Message, Modal } from 'core/ui-next/modal';
import { legacyControlsFor } from 'core/ui-next/controls';

type Props = Modal.Actions<void>;

type State = { gridHeight: number, ticketSummary: TicketSummaryDTO[] };

export async function canCloseDayWithPendingTickets(): Promise<boolean> {
	async function thereIsAnotherPosToClosePendingTickets() {
		const status = await RestaurantFacade.getPointsOfSaleStatus();
		const thereIsAnotherOpenPosToClosePendingTickets = status.some(x => x.Id !== SessionProvider.getCurrent().PosId && x.AllowCashTransactions && x.IsClosed === false);
		return thereIsAnotherOpenPosToClosePendingTickets;
	}

	function thereAreNoPendingTickets() {
		return CloseDayStore.getPendingTickets().length === 0;
	}

	function userHasPermissionToCloseWithPendingTickets() {
		return SessionProvider.hasPermission(Permission.Pos_CloseDay_With_Open_Tickets);
	}

	if (thereAreNoPendingTickets() || userHasPermissionToCloseWithPendingTickets() || (await thereIsAnotherPosToClosePendingTickets())) {
		return true;
	}

	await Message.show(Res.Msg.MSG_PERMISSION_DENIED_FOR(Permission.toString(Permission.Pos_CloseDay_With_Open_Tickets)));
	return false;
}

class EndPendingTicketsScreen extends PosScreenClassComponent<Props, State> {

	private grid = React.createRef<UI.DataGrid<TicketSummaryDTO>>();
	private ref = React.createRef<HTMLDivElement>();

	protected getScreenId(): string {
		return 'END_PENDING_TICKETS';
	}

	protected getTitle(): string {
		return Res.Web.END_PENDING_TICKETS();
	}

	protected getSize(): ScreenSize {
		return 'full-screen';
	}

	protected getImage(): string | SvgIcon {
		return Images.CloseDay;
	}

	protected getActionButtonsDefs(): Nullable<ActionButtonDefinition>[] {
		return [
			this.cancelButton(),
			null,
			CloseDayStore.getSelectedTicket() ? { action: this.closeTicket, image: Images.CloseTicket, title: Res.Web.END_PENDING_TICKETS_CLOSE_TICKET_BTN() } : null,
			CloseDayStore.getSelectedTicket() ? { action: this.cancelTicket, image: Images.CancelTicket, title: Res.Web.END_PENDING_TICKETS_CANCEL_TICKET_BTN() } : null,
			null,
			{ action: this.closeDay, image: Images.Next, title: Res.Common.NEXT_BTN() }
		];
	}

	onSelectedTicket = async (selectedTicket: Nullable<TicketSummaryDTO>) => {
		if (selectedTicket) {
			await CloseDayStore.select(selectedTicket);
			this.forceUpdate(() => this.grid.current?.selectItems([CloseDayStore.getSelectedSummary()!]));
		}
	};

	protected useKeypad() {
		return false;
	}

	createInitialState(props: Props): Nullable<State> {
		return {
			gridHeight: 400,
			ticketSummary: CloseDayStore.getPendingTickets(),
			...props
		};
	}

	async onComponentDidMount() {
		UI.ResizeListener.add(this.ref.current!, this.fixTableHeight);
		this.fixTableHeight();

		this.on('pos:sale:operation-failed', async ({ msg }) => {
			await Message.show(msg);
		});

		this.on('pos:close-day:operation-failed', async ({ msg }) => {
			await Message.show(msg);
		});

		const selectedSummary = CloseDayStore.getSelectedSummary();
		selectedSummary && this.grid.current!.selectItems([selectedSummary]);
	}

	onComponentWillUnmount() {
		UI.ResizeListener.remove(this.ref.current!);
	}

	fixTableHeight = () => {
		if (!this.mounted)
			return;

		const container = document.getElementById('gridContainer');
		this.setState({
			gridHeight: container!.clientHeight
		});
	};

	closeTicket = async () => {
		await Command.execute('closePendingTicket');
		await this.refreshPendingTickets();
	};

	cancelTicket = async () => {
		const selected = CloseDayStore.getSelectedTicket();
		if (!selected)
			return;

		await Command.execute('cancelTicket', {
			cancel: CloseDayStore.cancel,
			release: CloseDayStore.release,
			getTicket: () => selected
		});
		await this.refreshPendingTickets();
	};

	closeDay = async () => {
		if (await canCloseDayWithPendingTickets())
			this.close();
	};

	async refreshPendingTickets() {
		await CloseDayStore.reloadPendingTickets();
		this.setState({ ticketSummary: CloseDayStore.getPendingTickets() });
		this.forceUpdate(() => this.grid.current?.selectItems([CloseDayStore.getSelectedSummary()!]));
	}

	protected getContent(): React.ReactNode {

		const total = CloseDayStore.getSelectedTicket()?.NetAmount || 0;

		const { DataGrid } = legacyControlsFor<TicketSummaryDTO>();

		const columns: UI.DataGridColumn<TicketSummaryDTO>[] = [
			{ title: Res.Web.END_PENDING_TICKETS_TABLE_PAYMENT_REFERENCE_CLM(), linkTo: x => x.TablePaymentReference, width: 80 },
			{ title: Res.Web.END_PENDING_TICKETS_TABLE_PREPARATION_NUMBER_CLM(), linkTo: x => x.PreparationNumber, width: 80 },
			{ title: Res.Web.END_PENDING_TICKETS_TABLE_LOCATION_DESCRIPTION_CLM(), linkTo: x => x.SaleLocationName, width: 100 },
			{ title: Res.Web.END_PENDING_TICKETS_TABLE_USER_CLM(), linkTo: x => x.OpenerUser, width: 100 },
			{ title: Res.Web.END_PENDING_TICKETS_TABLE_DATE_CLM(), linkTo: x => x.CreationDate, format: UI.Format.asDate, width: 90 },
			{ title: Res.Web.END_PENDING_TICKETS_TABLE_HOUR_CLM(), linkTo: x => x.CreationDate, format: value => UI.Format.asTime(value), width: 90 },
			{ title: Res.Web.END_PENDING_TICKETS_TABLE_TOTAL_CLM(), linkTo: x => x.NetAmount, format: UI.Format.asMoney, width: 90, cellClassName: 'text-center', headerClassName: 'text-center' },
			{ title: Res.Web.END_PENDING_TICKETS_TABLE_PAID_CLM(), linkTo: x => x.PendingAmount, format: (_v, i) => UI.Format.asMoney(i.NetAmount - i.PendingAmount), width: 150, cellClassName: 'text-center', headerClassName: 'text-center' },
			{ title: Res.Web.END_PENDING_TICKETS_TABLE_CUSTOMER_CLM(), linkTo: x => x.CustomerFiscalName, width: 120 }
		];

		const preview = CloseDayStore.getPreview();

		return <Flex.Horizontal ref={this.ref}>
			<Flex.Item size='*' className='border-right' id='gridContainer' style={{ overflow: 'hidden' /* Necesario para calcular bien la altura a la primera */ }} >
				<DataGrid
					ref={this.grid}
					id='pendingTicketsDataGrid'
					initialData={this.state.ticketSummary}
					filtrable
					hideExportButton={true}
					multiSelect={false}
					height={this.state.gridHeight}
					itemKey={x => x.Id}
					columns={columns}
					selected={true}
					onSelectedItemsChanged={tickets => this.onSelectedTicket(tickets[0] || null)}
					shouldPersistState={false}
				/>
			</Flex.Item>
			<Flex.Item size='425'>
				{preview ?
					<Flex.Vertical>
						<Flex.Item size='*'>
							{preview && <UI.TemplatePrintPreview preview={preview} />}
						</Flex.Item>
						<Flex.Item size='55' className='border-top m-t-xs m-b-xs text-center font-bold text-32'>
							{Res.Web.END_PENDING_TICKETS_TOTAL_LBL(UI.Format.asMoney(total))}
						</Flex.Item>
					</Flex.Vertical>
					: <></> // Pongo un fragment vacío porque si pongo null Flex.Item no dibuja nada y quiero que deje un hueco en blanco cuando no hay ticket seleccionado
				}
			</Flex.Item>
		</Flex.Horizontal>;
	}
}

export default async function endPendingTickets(): Promise<Result> {
	if (CloseDayStore.getPendingTickets().length === 0)
		return Result.ok();

	return await Modal.show(EndPendingTicketsScreen);
}
